import { useEffect, useState, useRef } from "react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Radar, Check, X, RefreshCw, Settings as SettingsIcon, Globe } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

export default function DiscoveryPage() {
    const [discovered, setDiscovered] = useState([]);
    const [settings, setSettings] = useState(null);
    const [dashboardSettings, setDashboardSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [draft, setDraft] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const fileInputRef = useRef(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const [d, s, ds] = await Promise.all([
                api.get("/discovery"),
                api.get("/discovery/settings"),
                api.get("/settings/dashboard"),
            ]);
            setDiscovered(d.data);
            setSettings(s.data);
            setDashboardSettings(ds.data);
            setScanning(Boolean(s.data?.scanning));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 5000);
        return () => clearInterval(t);
    }, []);

    const triggerScan = async () => {
        try {
            const { data } = await api.post("/discovery/scan");
            if (data.started) {
                setScanning(true);
                toast.success("Discovery scan started");
            } else {
                toast.info(data.reason || "Scan already running");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const approve = async (item) => {
        try {
            await api.post(`/discovery/${item.id}/approve`);
            toast.success(`Added ${item.title || item.host}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const dismiss = async (item) => {
        try {
            await api.post(`/discovery/${item.id}/dismiss`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleBackgroundUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUploadError("");
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/settings/dashboard/background", form);
            setDashboardSettings({ background_image_url: data.background_image_url });
            toast.success("Background image uploaded");
        } catch (e) {
            setUploadError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
            event.target.value = "";
        }
    };

    const removeBackground = async () => {
        try {
            await api.delete("/settings/dashboard/background");
            toast.success("Background image removed");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const openSettings = () => {
        setDraft({
            enabled: settings?.enabled ?? false,
            scan_interval: settings?.scan_interval ?? 900,
            ip_range: settings?.ip_range ?? "192.168.0.0/24",
            ports: (settings?.ports || []).join(", "),
        });
        setSettingsOpen(true);
    };

    const saveSettings = async () => {
        try {
            await api.put("/discovery/settings", {
                enabled: draft.enabled,
                scan_interval: parseInt(draft.scan_interval, 10) || 900,
                ip_range: draft.ip_range,
                ports: draft.ports
                    .split(",")
                    .map((p) => parseInt(p.trim(), 10))
                    .filter((n) => Number.isInteger(n) && n > 0 && n < 65536),
            });
            toast.success("Discovery settings saved");
            setSettingsOpen(false);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <>
            <Header />
            <main className="max-w-[1600px] mx-auto px-6 sm:px-10 py-10 sm:py-12" data-testid="discovery-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-3">
                            Discovered Apps
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold">
                            Network discovery
                        </h1>
                        <p className="text-muted-foreground mt-2 max-w-xl">
                            Auto-detected web services on your local network. Approve to add to your dashboard.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={openSettings} className="rounded-full h-11" data-testid="open-discovery-settings">
                            <SettingsIcon className="h-4 w-4 mr-1.5" /> Settings
                        </Button>
                        <Button onClick={triggerScan} disabled={scanning} className="rounded-full h-11" data-testid="scan-now">
                            <RefreshCw className={`h-4 w-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                            {scanning ? "Scanning..." : "Scan now"}
                        </Button>
                    </div>
                </div>

                {settings && (
                    <>
                        <div className="glass-tile rounded-2xl p-5 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Auto-discovery:</span>
                            <span className="font-semibold">{settings.enabled ? "Enabled" : "Disabled"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">IP range:</span>
                            <span className="font-mono">{settings.ip_range}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Interval:</span>
                            <span className="font-mono">{settings.scan_interval}s</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Last scan:</span>
                            <span className="font-mono">
                                {settings.last_scan ? new Date(settings.last_scan).toLocaleString() : "Never"}
                            </span>
                        </div>
                    </div>

                    <div className="glass-tile rounded-2xl p-5 mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                            <div>
                                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                                    Dashboard background
                                </p>
                                <p className="text-sm text-muted-foreground max-w-2xl">
                                    Upload a custom image to replace the default dashboard background for all users.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    onClick={triggerFileSelect}
                                    disabled={uploading}
                                    data-testid="upload-background"
                                >
                                    {uploading ? "Uploading..." : "Upload image"}
                                </Button>
                                {dashboardSettings?.background_image_url && (
                                    <Button
                                        variant="ghost"
                                        onClick={removeBackground}
                                        data-testid="remove-background"
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                        </div>
                        {uploadError && <div className="text-sm text-red-500 mb-4">{uploadError}</div>}
                        {dashboardSettings?.background_image_url ? (
                            <div className="rounded-3xl overflow-hidden border border-border">
                                <img
                                    src={dashboardSettings.background_image_url}
                                    alt="Dashboard background preview"
                                    className="w-full h-48 object-cover"
                                />
                            </div>
                        ) : (
                            <div className="rounded-3xl border border-border p-6 text-sm text-muted-foreground">
                                No custom background set. The default image will remain in use.
                            </div>
                        )}
                    </div>
                </>
                )}

                {loading ? (
                    <div className="text-muted-foreground">Loading...</div>
                ) : discovered.length === 0 ? (
                    <div className="glass-tile rounded-3xl p-12 text-center">
                        <Radar className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                        <h3 className="font-display text-2xl font-semibold mb-2">No discovered apps</h3>
                        <p className="text-muted-foreground">
                            Trigger a scan or enable auto-discovery from settings.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
                        {discovered.map((d) => (
                            <div
                                key={d.id}
                                className="glass-tile tile-dashed rounded-3xl p-5 sm:p-6 relative"
                                data-testid={`discovered-${d.id}`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="h-12 w-12 rounded-2xl bg-foreground/10 flex items-center justify-center">
                                        {d.icon_url ? (
                                            <img
                                                src={d.icon_url}
                                                alt=""
                                                className="h-full w-full object-cover rounded-2xl"
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                }}
                                            />
                                        ) : (
                                            <Globe className="h-5 w-5 text-muted-foreground" />
                                        )}
                                    </div>
                                </div>
                                <h3 className="font-display font-semibold text-lg leading-tight truncate">
                                    {d.title || `${d.host}:${d.port}`}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1 font-mono truncate">
                                    {d.url}
                                </p>
                                <div className="mt-4 flex gap-2">
                                    <Button
                                        size="sm"
                                        className="flex-1 rounded-full"
                                        onClick={() => approve(d)}
                                        data-testid={`approve-${d.id}`}
                                    >
                                        <Check className="h-3.5 w-3.5 mr-1" /> Add
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1 rounded-full"
                                        onClick={() => dismiss(d)}
                                        data-testid={`dismiss-${d.id}`}
                                    >
                                        <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBackgroundUpload}
                    data-testid="background-file-input"
                />
            </main>

            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="discovery-settings-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Discovery settings</DialogTitle>
                        <DialogDescription>Configure how AppDash scans your network.</DialogDescription>
                    </DialogHeader>
                    {draft && (
                        <div className="space-y-5 py-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="ds-enabled" className="font-medium">
                                    Enable auto-discovery
                                </Label>
                                <Switch
                                    id="ds-enabled"
                                    checked={draft.enabled}
                                    onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                                    data-testid="switch-enable-discovery"
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label htmlFor="ds-range">IP range / subnet</Label>
                                <Input
                                    id="ds-range"
                                    value={draft.ip_range}
                                    onChange={(e) => setDraft({ ...draft, ip_range: e.target.value })}
                                    placeholder="192.168.0.0/24"
                                    data-testid="input-ip-range"
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label htmlFor="ds-interval">Scan interval (seconds)</Label>
                                <Input
                                    id="ds-interval"
                                    type="number"
                                    min={60}
                                    value={draft.scan_interval}
                                    onChange={(e) =>
                                        setDraft({ ...draft, scan_interval: e.target.value })
                                    }
                                    data-testid="input-scan-interval"
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label htmlFor="ds-ports">Ports (comma separated)</Label>
                                <Input
                                    id="ds-ports"
                                    value={draft.ports}
                                    onChange={(e) => setDraft({ ...draft, ports: e.target.value })}
                                    placeholder="80, 443, 3000, 5000, 8000, 8080"
                                    data-testid="input-ports"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setSettingsOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={saveSettings} data-testid="save-discovery-settings">
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
